import { useState, useEffect, useMemo, Fragment } from 'react';
import {
  ChevronUp,
  ChevronDown,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Settings as SettingsIcon,
  Eye,
  EyeOff,
  Inbox,
  ChevronRight as ChevronRightIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Row actions pinned to the right edge WHILE THE TABLE OVERFLOWS its scroller
// (opaque so the scrolled columns don't show through). Driven by measured
// overflow, not a viewport breakpoint: the table is min 900px, so a tablet or
// a desktop with the Records sidebar overflows too. When the table fits, the
// cells stay plain so the row's hover and selection tints still apply.
const PIN_CLASSES = 'sticky right-0 z-10 border-l border-border/50 bg-card';

export interface Column<T> {
  key: string;
  label: React.ReactNode;
  sortable?: boolean;
  render?: (item: T) => React.ReactNode;
  width?: string; // CSS width (e.g., '200px', '40%')
  align?: 'left' | 'center' | 'right'; // Text alignment (default: 'left')
}

export interface Action<T> {
  label: string;
  onClick: (item: T) => void;
  variant?: 'default' | 'destructive';
  icon?: React.ElementType;
  show?: (item: T) => boolean;
}

export interface BulkAction<T> {
  label: string;
  onClick: (items: T[]) => void;
  variant?: 'default' | 'destructive';
  icon?: React.ElementType;
  disabled?: boolean;
}

interface AdminDataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  actions?: Action<T>[];
  bulkActions?: BulkAction<T>[];
  onRowClick?: (item: T) => void;
  emptyMessage?: string;
  loading?: boolean;
  searchable?: boolean;
  idField?: string; // Field to use as unique ID (default: 'id')
  tableId: string; // Unique identifier for persisting table preferences
  minRows?: number; // Minimum rows to display (adds empty rows for consistent height)
  itemLabel?: string; // Label for the count (e.g., "rate", "provider"). If not set, uses "item"
  defaultPageSize?: number; // Default page size if no preference stored (default: 10)
  // Expandable row support
  expandable?: boolean; // Enable expandable rows
  canExpand?: (item: T) => boolean; // Function to determine if a row can expand
  onRowExpand?: (item: T, isExpanded: boolean) => void; // Callback when row is expanded/collapsed (for lazy loading)
  expandedRowRender?: (item: T) => React.ReactNode; // Render function for expanded content
  expandedRowLoading?: (item: T) => boolean; // Function to check if expanded content is loading
}

export function AdminDataTable<T extends object>({
  data,
  columns,
  actions,
  bulkActions,
  onRowClick,
  emptyMessage = 'No data found',
  loading = false,
  idField = 'id',
  tableId,
  minRows = 0,
  itemLabel = 'item',
  defaultPageSize = 10,
  expandable = false,
  canExpand,
  onRowExpand,
  expandedRowRender,
  expandedRowLoading
}: AdminDataTableProps<T>) {
  // Storage keys
  const SORT_KEY = `table_${tableId}_sort`;
  const PAGE_SIZE_KEY = `table_${tableId}_pageSize`;
  const VISIBLE_COLUMNS_KEY = `table_${tableId}_visibleColumns`;

  // Load persisted preferences
  const loadPreferences = () => {
    try {
      const savedSort = localStorage.getItem(SORT_KEY);
      const savedPageSize = localStorage.getItem(PAGE_SIZE_KEY);
      const savedVisibleColumns = localStorage.getItem(VISIBLE_COLUMNS_KEY);

      // Parse saved visible columns
      let visibleCols = savedVisibleColumns ? JSON.parse(savedVisibleColumns) : columns.map(c => c.key);

      // IMPORTANT: Always include new columns that weren't in saved preferences
      // This ensures newly added columns appear automatically instead of being hidden
      const allColumnKeys = columns.map(c => c.key);
      const newColumns = allColumnKeys.filter(key => !visibleCols.includes(key));
      if (newColumns.length > 0) {
        visibleCols = [...visibleCols, ...newColumns];
        // Update localStorage with the new columns included
        localStorage.setItem(VISIBLE_COLUMNS_KEY, JSON.stringify(visibleCols));
      }

      return {
        sort: savedSort ? JSON.parse(savedSort) : { key: null, direction: 'asc' },
        pageSize: savedPageSize ? parseInt(savedPageSize, 10) : defaultPageSize,
        visibleColumns: visibleCols
      };
    } catch (error) {
      console.error('Failed to load table preferences:', error);
      return {
        sort: { key: null, direction: 'asc' },
        pageSize: defaultPageSize,
        visibleColumns: columns.map(c => c.key)
      };
    }
  };

  const prefs = loadPreferences();

  // Sorting
  const [sortKey, setSortKey] = useState<string | null>(prefs.sort.key);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(prefs.sort.direction as 'asc' | 'desc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(prefs.pageSize);

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(new Set(prefs.visibleColumns));
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Expanded rows
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Whether the table is wider than its horizontal scroller right now. A
  // callback ref (state setter) so the observer attaches once the scroller
  // mounts — it does not exist while loading or empty.
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  const [tableOverflows, setTableOverflows] = useState(false);
  useEffect(() => {
    if (!scroller || typeof ResizeObserver === 'undefined') return;
    const measure = () => setTableOverflows(scroller.scrollWidth > scroller.clientWidth + 1);
    const observer = new ResizeObserver(measure);
    observer.observe(scroller);
    const table = scroller.firstElementChild;
    if (table) observer.observe(table);
    return () => observer.disconnect();
  }, [scroller]);

  // Save sorting preferences
  useEffect(() => {
    localStorage.setItem(SORT_KEY, JSON.stringify({ key: sortKey, direction: sortDirection }));
  }, [sortKey, sortDirection, SORT_KEY]);

  // Save page size preference
  useEffect(() => {
    localStorage.setItem(PAGE_SIZE_KEY, pageSize.toString());
  }, [pageSize, PAGE_SIZE_KEY]);

  // Save column visibility preference
  useEffect(() => {
    localStorage.setItem(VISIBLE_COLUMNS_KEY, JSON.stringify(Array.from(visibleColumns)));
  }, [visibleColumns, VISIBLE_COLUMNS_KEY]);

  // Reset page when data length changes
  useEffect(() => {
    setCurrentPage(1);
  }, [data.length]);

  // Reset selection when data changes significantly (optional, depends on UX preference)
  useEffect(() => {
    setSelectedIds(new Set());
  }, [data]);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  // 1. Sort Data
  const sortedData = useMemo(() => {
    if (!sortKey) return data;
    
    return [...data].sort((a, b) => {
      const aVal = (a as Record<string, unknown>)[sortKey];
      const bVal = (b as Record<string, unknown>)[sortKey];

      if (aVal === bVal) return 0;
      if (sortDirection === 'asc') {
        return (aVal as number) > (bVal as number) ? 1 : -1;
      } else {
        return (aVal as number) < (bVal as number) ? 1 : -1;
      }
    });
  }, [data, sortKey, sortDirection]);

  // 2. Paginate Data
  const totalPages = Math.ceil(sortedData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentData = sortedData.slice(startIndex, endIndex);

  // Selection Handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = currentData.map(item => (item as Record<string, unknown>)[idField] as string);
      setSelectedIds(new Set([...selectedIds, ...allIds]));
    } else {
      const newSelected = new Set(selectedIds);
      currentData.forEach(item => newSelected.delete((item as Record<string, unknown>)[idField] as string));
      setSelectedIds(newSelected);
    }
  };

  const handleSelectRow = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };

  // Column visibility handlers
  const toggleColumn = (columnKey: string) => {
    const newVisible = new Set(visibleColumns);
    if (newVisible.has(columnKey)) {
      // Don't allow hiding all columns
      if (newVisible.size > 1) {
        newVisible.delete(columnKey);
      }
    } else {
      newVisible.add(columnKey);
    }
    setVisibleColumns(newVisible);
  };

  const showAllColumns = () => {
    setVisibleColumns(new Set(columns.map(c => c.key)));
  };

  // Expand toggle handler
  const toggleExpand = (id: string, item: T) => {
    const newExpanded = new Set(expandedIds);
    const isCurrentlyExpanded = newExpanded.has(id);

    if (isCurrentlyExpanded) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedIds(newExpanded);

    // Call callback for lazy loading
    onRowExpand?.(item, !isCurrentlyExpanded);
  };

  const getSelectedItems = () => {
    return data.filter(item => selectedIds.has((item as Record<string, unknown>)[idField] as string));
  };

  const isAllCurrentPageSelected = currentData.length > 0 && currentData.every(item => selectedIds.has((item as Record<string, unknown>)[idField] as string));

  // Filter columns to only visible ones
  const displayColumns = columns.filter(col => visibleColumns.has(col.key));
  const hasRowActions = Boolean(actions && actions.length > 0);
  const pinActions = hasRowActions && tableOverflows;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 border border-border rounded-lg bg-card/50">
        <Inbox className="w-12 h-12 text-muted-foreground/50 mb-4" />
        <div className="text-muted-foreground text-sm">{emptyMessage}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Bulk Actions Bar */}
      {selectedIds.size > 0 && bulkActions && (
        <div className="bg-[#8FB560]/10 border border-[#8FB560]/20 rounded-lg p-3 flex items-center justify-between animate-in fade-in slide-in-from-top-2">
          <span className="text-sm text-[#8FB560] font-medium px-2">
            {selectedIds.size} selected
          </span>
          <div className="flex gap-2">
            {bulkActions.map((action, i) => (
              <Button
                key={i}
                size="sm"
                variant={action.variant === 'destructive' ? 'destructive' : 'secondary'}
                onClick={() => {
                  action.onClick(getSelectedItems());
                  setSelectedIds(new Set()); // Clear selection after action
                }}
                className="h-8 text-xs"
                disabled={action.disabled}
              >
                {action.icon && <action.icon className="w-3 h-3 mr-2" />}
                {action.label}
              </Button>
            ))}
          </div>
        </div>
      )}

      <div className="border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto" ref={setScroller}>
        <table className="w-full min-w-[900px]">
          <thead className="bg-card border-b border-border">
            <tr>
              {/* Expand Column - for expandable rows */}
              {expandable && (
                <th className="w-[32px] pl-3 pr-1 py-3" />
              )}

              {/* Checkbox Column */}
              {bulkActions && (
                <th className="w-[32px] pl-3 pr-1 py-3">
                  <Checkbox
                    checked={isAllCurrentPageSelected}
                    onCheckedChange={(checked) => handleSelectAll(checked as boolean)}
                    aria-label="Select all"
                  />
                </th>
              )}

              {displayColumns.map((column, colIndex) => {
                const alignClass = column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left';
                const justifyClass = column.align === 'right' ? 'justify-end' : column.align === 'center' ? 'justify-center' : 'justify-start';
                // Centered columns get minimal symmetric padding, column after centered gets reduced left padding
                const prevColumn = colIndex > 0 ? displayColumns[colIndex - 1] : null;
                const afterCentered = prevColumn?.align === 'center';
                const paddingClass = column.align === 'center' ? 'px-1' : (afterCentered ? 'pl-1 pr-3' : 'px-3');
                return (
                  <th
                    key={column.key}
                    className={`${paddingClass} py-3 ${alignClass} text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap`}
                    style={column.width ? { width: column.width, minWidth: column.width } : undefined}
                  >
                    {/* Only wrap sortable columns in a button to avoid nested button issues */}
                    {column.sortable !== false ? (
                      <button
                        onClick={() => handleSort(column.key)}
                        className={`flex items-center gap-1 hover:text-foreground transition-colors group ${justifyClass} w-full`}
                      >
                        <span>{column.label}</span>
                        {sortKey === column.key ? (
                          sortDirection === 'asc' ? (
                            <ChevronUp className="w-3 h-3 flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-3 h-3 flex-shrink-0" />
                          )
                        ) : (
                          <span className="w-3 h-3 flex-shrink-0" />
                        )}
                      </button>
                    ) : (
                      <div className={`flex items-center ${justifyClass} w-full`}>
                        {column.label}
                      </div>
                    )}
                  </th>
                );
              })}
              {/* Column settings - always visible. Pinned to the right edge on
                  phones (as the row actions below are), released from md up. */}
              <th
                data-testid={hasRowActions ? 'actions-header-cell' : undefined}
                className={`${pinActions ? PIN_CLASSES : ''} px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider`}
              >
                <div className="flex items-center justify-end gap-2">
                  {actions && actions.length > 0 && <span>Actions</span>}
                  <DropdownMenu open={columnMenuOpen} onOpenChange={setColumnMenuOpen}>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Column settings">
                        <SettingsIcon className="w-3 h-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-card border-border w-48">
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-b border-border">
                        Column Visibility
                      </div>
                      {columns.map((column) => (
                        <DropdownMenuItem
                          key={column.key}
                          onClick={(e) => {
                            e.preventDefault();
                            toggleColumn(column.key);
                          }}
                          className="cursor-pointer flex items-center gap-2"
                        >
                          {visibleColumns.has(column.key) ? (
                            <Eye className="w-3 h-3 text-[#8FB560]" />
                          ) : (
                            <EyeOff className="w-3 h-3 text-muted-foreground/50" />
                          )}
                          <span className={visibleColumns.has(column.key) ? 'text-foreground' : 'text-muted-foreground'}>
                            {column.label}
                          </span>
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.preventDefault();
                          showAllColumns();
                        }}
                        className="cursor-pointer border-t border-border text-[#8FB560]"
                      >
                        Show All Columns
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {currentData.map((item, index) => {
              const itemId = (item as Record<string, unknown>)[idField] as string;
              const isExpanded = expandedIds.has(itemId);
              const canItemExpand = expandable && (!canExpand || canExpand(item));
              const isLoading = expandedRowLoading?.(item) ?? false;
              // Calculate total columns for expanded row colspan
              const totalColumns = displayColumns.length + (bulkActions ? 1 : 0) + (expandable ? 1 : 0) + 1; // +1 for actions column

              return (
                <Fragment key={itemId || index}>
                  <tr
                    className={`${
                      onRowClick ? 'cursor-pointer hover:bg-muted/50' : ''
                    } ${selectedIds.has(itemId) ? 'bg-muted/30' : ''} ${isExpanded ? 'bg-muted/20' : ''} transition-colors`}
                    onClick={() => onRowClick?.(item)}
                  >
                    {/* Expand Toggle Cell */}
                    {expandable && (
                      <td className="pl-3 pr-1 py-3" onClick={(e) => e.stopPropagation()}>
                        {canItemExpand && (
                          <button
                            onClick={() => toggleExpand(itemId, item)}
                            className="w-6 h-6 flex items-center justify-center rounded hover:bg-muted transition-colors"
                            aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                          >
                            <ChevronRightIcon
                              className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${
                                isExpanded ? 'rotate-90' : ''
                              }`}
                            />
                          </button>
                        )}
                      </td>
                    )}

                    {/* Checkbox Cell */}
                    {bulkActions && (
                      <td className="pl-3 pr-1 py-3" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.has(itemId)}
                          onCheckedChange={(checked) => handleSelectRow(itemId, checked as boolean)}
                          aria-label={`Select row`}
                        />
                      </td>
                    )}

                    {displayColumns.map((column, colIndex) => {
                      const alignClass = column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left';
                      const prevColumn = colIndex > 0 ? displayColumns[colIndex - 1] : null;
                      const afterCentered = prevColumn?.align === 'center';
                      const paddingClass = column.align === 'center' ? 'px-1' : (afterCentered ? 'pl-1 pr-3' : 'px-3');
                      return (
                        <td
                          key={column.key}
                          className={`${paddingClass} py-3 text-sm text-foreground ${alignClass}`}
                          style={column.width ? { width: column.width, minWidth: column.width } : undefined}
                        >
                          {column.render ? column.render(item) : ((item as Record<string, unknown>)[column.key] as React.ReactNode)}
                        </td>
                      );
                    })}

                    {/* Actions or empty cell for column settings alignment. With
                        actions it is pinned to the right edge on phones: it is the
                        last column of a 900px+ table, so otherwise every row had to
                        be scrolled sideways to reach its ⋯ menu. Released from md up. */}
                    <td
                      data-testid={hasRowActions ? 'row-actions-cell' : undefined}
                      className={`${pinActions ? PIN_CLASSES : ''} px-4 py-3 text-right`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {actions && actions.length > 0 && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-card border-border">
                            {actions.filter(a => !a.show || a.show(item)).map((action, i) => (
                              <DropdownMenuItem
                                key={i}
                                onClick={() => action.onClick(item)}
                                className={`cursor-pointer ${
                                  action.variant === 'destructive'
                                    ? 'text-red-400 focus:text-red-400 focus:bg-red-500/10'
                                    : 'text-foreground focus:bg-muted'
                                }`}
                              >
                                {action.icon && <action.icon className="w-4 h-4 mr-2" />}
                                {action.label}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </td>
                  </tr>

                  {/* Expanded Row Content */}
                  {isExpanded && expandedRowRender && (
                    <tr className="bg-[#8FB560]/10">
                      <td colSpan={totalColumns} className="px-4 py-4 border-t border-[#8FB560]/20 border-b border-b-[#8FB560]/20">
                        <div className="bg-[#8FB560]/5 rounded-lg p-4 border border-[#8FB560]/15">
                          {isLoading ? (
                            <div className="flex items-center gap-2 py-2 text-sm text-[#8FB560]">
                              <div className="w-4 h-4 border-2 border-[#8FB560]/30 border-t-[#8FB560] rounded-full animate-spin" />
                              Loading details...
                            </div>
                          ) : (
                            expandedRowRender(item)
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {/* Empty placeholder rows for consistent height */}
            {minRows > 0 && currentData.length < minRows &&
              Array.from({ length: minRows - currentData.length }).map((_, i) => (
                <tr key={`empty-${i}`} className="pointer-events-none">
                  {expandable && <td className="pl-3 pr-1 py-3">&nbsp;</td>}
                  {bulkActions && <td className="pl-3 pr-1 py-3">&nbsp;</td>}
                  {displayColumns.map((column, colIndex) => {
                    const alignClass = column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left';
                    const prevColumn = colIndex > 0 ? displayColumns[colIndex - 1] : null;
                    const afterCentered = prevColumn?.align === 'center';
                    const paddingClass = column.align === 'center' ? 'px-1' : (afterCentered ? 'pl-1 pr-3' : 'px-3');
                    return (
                      <td
                        key={column.key}
                        className={`${paddingClass} py-3 text-sm ${alignClass}`}
                        style={column.width ? { width: column.width, minWidth: column.width } : undefined}
                      >
                        &nbsp;
                      </td>
                    );
                  })}
                  <td className={`${pinActions ? PIN_CLASSES : ''} px-4 py-3`}>&nbsp;</td>
                </tr>
              ))
            }
          </tbody>
        </table>
        </div>
      </div>

      {/* Pagination Footer - page size on left, count in center, navigation on right */}
      {/* flex-wrap: at phone width the three groups do not fit on one line and
          the last-page button was pushed off screen. */}
      <div data-pagination-footer className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-2">
        {/* Left: Rows per page */}
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <span className="whitespace-nowrap hidden sm:inline">Rows per page</span>
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => {
              setPageSize(Number(value));
              setCurrentPage(1);
            }}
          >
            <SelectTrigger className="h-8 w-[70px] bg-card border-border">
              <SelectValue placeholder={pageSize} />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              {/* Always include the current size: a caller's default (Check-Ins
                  asks for 25) that is not a standard option otherwise has no
                  matching item, and the select renders blank. */}
              {Array.from(new Set([10, 20, 50, 100, pageSize])).sort((a, b) => a - b).map((size) => (
                <SelectItem key={size} value={size.toString()} className="focus:bg-muted">
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Center: Total count */}
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          {data.length} {itemLabel}{data.length !== 1 ? 's' : ''}
        </span>

        {/* Right: Navigation */}
        <div className="flex items-center space-x-1 sm:space-x-2">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 border-border bg-card hover:bg-muted"
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 border-border bg-card hover:bg-muted"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground min-w-[4rem] text-center whitespace-nowrap">
            {currentPage} / {Math.max(1, totalPages)}
          </span>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 border-border bg-card hover:bg-muted"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 border-border bg-card hover:bg-muted"
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
